/**
 * Ask Argus — Memories page (ASK-M1 T3, founder decisions D1 / D-new / D3 /
 * D-notice, 2026-11-03).
 *
 * Route: /ask-argus/memories (reached from the chat sidebar, beside Projects).
 *
 * ONE list: every memory Argus holds about the signed-in person — the
 * user_memories rows the chat recalls from, nothing else (D1). Each row can be
 * re-worded (the API re-embeds it, so the chat recalls the new wording) or
 * deleted; "Forget everything" deletes them all (typed confirmation).
 * Memories are saved silently during chats and managed here (D-notice).
 *
 * A row's classification badge renders ONLY when the tenant has a compliance
 * framework active (GET returns `frameworkActive` from the one framework
 * resolver); the labels are the row's own (D3).
 *
 * All data is per-person: the API scopes every row to the caller.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Edit2, Trash2, Clock, Database, Shield, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { useAuth } from '@/contexts/auth-context';

export const Route = createFileRoute('/ask-argus/memories')({
  component: MemoriesPage,
});

/* ───────────────────────── auth gate ──────────────────────────── */

/**
 * The race condition this gate protects against:
 *
 *   1. User navigates to /ask-argus/memories.
 *   2. <AuthProvider> mounts and fires GET /api/v1/users/{uid} to
 *      resolve the JWT subject into a real user record.
 *   3. Before that resolves, this route's list mounts and its useQuery
 *      would fire GET /api/v1/ask-argus/memories.
 *   4. SPA navigation/strict-mode remount can abort the auth fetch
 *      mid-flight, leaving user.id empty and isAuthenticated=false.
 *   5. A fetch that ran in step 3 either 401s or surfaces a generic error.
 *
 * Gate: only enable the React Query fetch once the auth context reports it
 * has finished loading AND has a non-empty user.id.
 *
 * Exported so the regression test can exercise the gate's truth
 * table without spinning up a renderer (the workspace deliberately
 * ships without @testing-library/react).
 */
export function isMemoryQueryEnabled(args: {
  authLoading: boolean;
  userId: string | null | undefined;
}): boolean {
  return !args.authLoading && !!args.userId;
}

/* ────────────────────────── types ─────────────────────────────── */

export interface MemoryClassification {
  sensitivity: string;
  exportControl: string;
}

export interface MemoryRow {
  id: string;
  content: string;
  category: string;
  source: string;
  classification: MemoryClassification;
  sourceConversationId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MemoriesResponse {
  frameworkActive: boolean;
  rows: MemoryRow[];
}

/** The phrase the Forget everything dialog asks for — the API checks it too. */
export const FORGET_EVERYTHING_PHRASE = 'FORGET EVERYTHING';

/**
 * The badge decision (the LB6 rule: classification controls are hidden when
 * no compliance framework is active). Returns the row's own labels to render,
 * or null for no badge.
 */
export function memoryBadgeFor(
  row: Pick<MemoryRow, 'classification'>,
  frameworkActive: boolean,
): MemoryClassification | null {
  return frameworkActive ? row.classification : null;
}

/** How a memory came to be, in the person's words. */
export function memorySourceLabel(source: string): string {
  if (source === 'explicit') return 'You asked Argus to remember';
  if (source === 'auto_extraction') return 'Noticed in a chat';
  return source;
}

/** The rows whose content or category contains the filter text. */
export function filterMemories(rows: readonly MemoryRow[], filter: string): MemoryRow[] {
  const q = filter.trim().toLowerCase();
  if (!q) return [...rows];
  return rows.filter(
    (r) => r.content.toLowerCase().includes(q) || r.category.toLowerCase().includes(q),
  );
}

/* ─────────────────────── time helpers ─────────────────────────── */

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

/* ────────────────────────── page ──────────────────────────────── */

function MemoriesPage() {
  const [forgetOpen, setForgetOpen] = useState(false);

  return (
    <div className="container mx-auto py-6 max-w-5xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Your Memories</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Argus remembers what you say about yourself and how you work — your
            role, your site or line, the units you use, how you like answers.
            It never remembers its own answers, and never facts about equipment,
            parts, readings or procedures — those come from the Library, with
            sources. Memories are saved quietly as you chat; edit or delete them
            here.
          </p>
        </div>
        <Button
          variant="destructive"
          onClick={() => setForgetOpen(true)}
          title="Delete every memory Argus has about you"
          data-testid="forget-all-memories-button"
        >
          <AlertTriangle className="size-4 mr-2" />
          Forget everything
        </Button>
      </div>

      <MemoryList />

      <ForgetEverythingDialog open={forgetOpen} onOpenChange={setForgetOpen} />
    </div>
  );
}

/* ────────────────────────── the list ──────────────────────────── */

function MemoryList() {
  const queryClient = useQueryClient();
  const { user, isLoading: authLoading } = useAuth();
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const authReady = isMemoryQueryEnabled({ authLoading, userId: user.id });

  const { data, isLoading, error } = useQuery({
    queryKey: ['ask-argus-memories', user.id],
    queryFn: () => apiClient.get<MemoriesResponse>('/ask-argus/memories'),
    enabled: authReady,
  });

  const editMutation = useMutation({
    mutationFn: async ({ id, content }: { id: string; content: string }) =>
      apiClient.patch(`/ask-argus/memories/${id}`, { content }),
    onSuccess: () => {
      toast.success('Memory updated');
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ['ask-argus-memories'] });
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to update the memory')),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiClient.delete(`/ask-argus/memories/${id}`),
    onSuccess: () => {
      toast.success('Memory deleted');
      queryClient.invalidateQueries({ queryKey: ['ask-argus-memories'] });
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to delete the memory')),
  });

  if (!authReady || isLoading) {
    return <div className="text-muted-foreground text-sm">Loading memories…</div>;
  }
  if (error) return <div className="text-destructive text-sm">Failed to load your memories.</div>;

  const rows = data?.rows ?? [];
  const frameworkActive = data?.frameworkActive === true;
  const filtered = filterMemories(rows, filter);

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          <Database className="size-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium">Argus doesn't remember anything about you yet</p>
          <p className="text-sm mt-1">
            Tell Argus about yourself in a chat — your role, your line, your
            preferred units — and it will show up here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="memories-list">
      <div className="flex items-center gap-2">
        <Search className="size-4 text-muted-foreground" />
        <Input
          placeholder="Filter memories…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-md"
        />
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} of {rows.length} memories
        </span>
      </div>
      <div className="space-y-3">
        {filtered.map((row) => {
          const badge = memoryBadgeFor(row, frameworkActive);
          return (
            <Card key={row.id} data-testid="memory-row">
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {editingId === row.id ? (
                      <Textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="text-sm"
                        maxLength={2000}
                        rows={3}
                      />
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{row.content}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <Badge variant="secondary" className="text-xs">
                        {row.category}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {memorySourceLabel(row.source)}
                      </span>
                      {badge && <ClassificationBadge tag={badge} />}
                    </div>
                    <div
                      className="flex items-center gap-1 mt-2 text-xs text-muted-foreground"
                      title={`Saved ${formatDate(row.createdAt)} · updated ${formatDate(row.updatedAt)}`}
                    >
                      <Clock className="size-3" />
                      {row.updatedAt !== row.createdAt
                        ? `Updated ${timeAgo(row.updatedAt)}`
                        : `Saved ${timeAgo(row.createdAt)}`}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {editingId === row.id ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          disabled={
                            editMutation.isPending ||
                            editValue.trim().length === 0 ||
                            editValue.trim() === row.content
                          }
                          onClick={() =>
                            editMutation.mutate({ id: row.id, content: editValue.trim() })
                          }
                        >
                          Save
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Edit this memory"
                          onClick={() => {
                            setEditingId(row.id);
                            setEditValue(row.content);
                          }}
                        >
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Delete this memory"
                          disabled={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(row.id)}
                        >
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────── shared bits ─────────────────────────── */

function ClassificationBadge({ tag }: { tag: MemoryClassification }) {
  const isClassified = tag.sensitivity !== 'unclassified' || tag.exportControl !== 'none';
  return (
    <Badge
      variant={isClassified ? 'destructive' : 'outline'}
      className="text-xs"
      title={`Sensitivity: ${tag.sensitivity} · Export Control: ${tag.exportControl}`}
    >
      <Shield className="size-3 mr-1" />
      {tag.sensitivity}
      {tag.exportControl !== 'none' && ` / ${tag.exportControl}`}
    </Badge>
  );
}

/* ──────────────────── forget everything dialog ─────────────────── */

function ForgetEverythingDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [confirmText, setConfirmText] = useState('');

  const mutation = useMutation({
    mutationFn: async () =>
      apiClient.delete<{ deleted: number }>('/ask-argus/memories', {
        body: JSON.stringify({ confirm: FORGET_EVERYTHING_PHRASE }),
      }),
    onSuccess: (result) => {
      toast.success(`Forgot everything: ${result.deleted} memories deleted.`);
      onOpenChange(false);
      setConfirmText('');
      queryClient.invalidateQueries({ queryKey: ['ask-argus-memories'] });
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Forget everything failed')),
  });

  const canConfirm = confirmText === FORGET_EVERYTHING_PHRASE;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-destructive" />
            Forget everything?
          </DialogTitle>
          <DialogDescription>
            This permanently deletes every memory Argus holds about you. Argus
            will start fresh in your next chat.
            <br />
            <br />
            Your conversation history is not affected. The deletion is recorded
            in your organization's audit log.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Type <code className="bg-muted px-1 py-0.5 rounded text-xs">{FORGET_EVERYTHING_PHRASE}</code> to confirm.
          </p>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={FORGET_EVERYTHING_PHRASE}
            className="font-mono"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!canConfirm || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Forget everything
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
